import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import LeftSidebar from "./LeftSidebar";
import { useSearchParams } from "react-router-dom";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import { getProjectDashboardPageUrl, getProjectSettingsPageUrl } from "@Features/projects/utils/projectUrls";
import { setShowStickySidebar, setSidebarSelectedCell } from "@Reducers/stickySidebar";
import { untranslated, useTranslation, type TranslationKey } from "@i18n";
import _ from "lodash";
import PermissionGate from "@Pages/Error/PermissionGate";
import { RootState } from "@Reducers/index";
import {
  MODULE_TAB_TYPE,
  MODULE_SETTING_TYPE,
} from "@Features/portal-settings/components/PortalSetings/Configuration/Modules/constants";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import { defaultModuleNamesMapper } from "@Helpers/constants";

/**
 * Settings-tab key → the lazily loaded panel for it. Every panel takes a `projectId` prop, so the
 * value type is narrowed to that rather than left open — `Component` is looked up by a runtime tab
 * key, which is why the map is indexed rather than destructured.
 */
const SETTINGS_COMPONENTS: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<{ projectId: ObjectIdString }>>
> = {
  resource_type: lazy(() => import("@Components/Resource/ResourceType")),
  currency: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectCurrencySetting }))),
  spaces: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectSpacesList }))),
  roles: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectRolesList }))),
  users: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectUsersList }))),
  groups: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectGroupsList }))),
  teams: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectTeams }))),
  inspection_location: lazy(() =>
    import("@Features/projects").then((module) => ({ default: module.ProjectInspectionLocation })),
  ),
  // `ProjectWBS` is a `forwardRef` component, so its type is a `ForwardRefExoticComponent` rather
  // than the `ComponentType<{ projectId }>` the rest of this map infers. Erased intersection
  // assertion at this one entry, same shape as the Router union-of-lazy-components fix.
  WBS: lazy(() =>
    import("@Features/projects").then((module) => ({
      default: module.ProjectWBS as typeof module.ProjectWBS & React.ComponentType<{ projectId: ObjectIdString }>,
    })),
  ),
  general_information: lazy(() => import("./GeneralProjectInformation")),
  formTemplates: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectFormTemplates }))),
  space: lazy(() => import("@Features/projects").then((module) => ({ default: module.spaceDetails }))),
  assetTypes: lazy(() => import("@Features/projects").then((module) => ({ default: module.assetTypes }))),
  userSpaces: lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectUserSpaces }))),
  projectModules: lazy(() => import("./ProjectModules")),
  formFlows: lazy(() => import("@Features/projects").then((module) => ({ default: module.FormFlows }))),
  reviewFlows: lazy(() => import("@Features/projects").then((module) => ({ default: module.ReviewFlows }))),
  mailConfiguration: lazy(() => import("@Features/projects").then((module) => ({ default: module.mailConfiguration }))),
  mailVisibility: lazy(() => import("@Features/projects").then((module) => ({ default: module.mailVisibility }))),
  mailWhitelist: lazy(() => import("@Features/projects").then((module) => ({ default: module.mailWhitelist }))),
};

/**
 * Breadcrumb label for each `tab` (`settingContent`) route key. The raw values are internal keys
 * ("general_information", "projectModules", "mailWhitelist"), so they are mapped to the same
 * wording the left sidebar and the module cards use instead of being printed verbatim.
 */
const SETTING_CONTENT_LABELS: Record<string, TranslationKey> = {
  general_information: "Project Information",
  WBS: "WBS",
  users: "Project Users",
  // Drill-down views of a tab keep their parent's label — the row they drilled into is named by
  // the next crumb, not by this one.
  userSpaces: "Project Users",
  groups: "Groups",
  spaces: "Project Spaces",
  space: "Project Spaces",
  roles: "Roles",
  teams: "Teams",
  currency: "Currency",
  resource_type: "Resource Type",
  projectModules: "Modules",
  inspection_location: "Location",
  formTemplates: "Form Templates",
  assetTypes: "Asset Types",
  formFlows: "Form Flows",
  reviewFlows: "Review Flows",
  mailConfiguration: "Configuration",
  mailVisibility: "Visibility",
  mailWhitelist: "Whitelist",
};

/**
 * Breadcrumb label for each `settingType` route key — the module sub-tab reached from the Modules
 * page. Keyed by `MODULE_TAB_TYPE`; the wording matches the sub-tab links on the module cards
 * (`getDefaultModuleMapper`), so the crumb reads the same as the link that was clicked.
 */
const SETTING_TYPE_LABELS: Record<string, TranslationKey> = {
  [MODULE_TAB_TYPE.FIELDS]: "Fields",
  [MODULE_TAB_TYPE.CUSTOM_METADATA]: "Custom Metadata",
  [MODULE_TAB_TYPE.NOMENCLATURE]: "Nomenclature",
  [MODULE_TAB_TYPE.FORM_TEMPLATES]: "Form Templates",
  [MODULE_TAB_TYPE.FORM_FLOWS]: "Form Flows",
  [MODULE_TAB_TYPE.REVIEW_FLOWS]: "Review Flows",
  [MODULE_TAB_TYPE.MAIL_WHITELIST]: "Whitelist",
  [MODULE_TAB_TYPE.MAIL_CONFIGURATION]: "Configuration",
  [MODULE_TAB_TYPE.MAIL_VISIBILITY]: "Visibility",
  [MODULE_TAB_TYPE.ASSET_TYPES]: "Asset Types",
};

const PROJECT_MODULE_TAB_TYPES: string[] = [
  MODULE_TAB_TYPE.FORM_FLOWS,
  MODULE_TAB_TYPE.REVIEW_FLOWS,
  MODULE_TAB_TYPE.FORM_TEMPLATES,
  MODULE_TAB_TYPE.MAIL_CONFIGURATION,
  MODULE_TAB_TYPE.MAIL_VISIBILITY,
  MODULE_TAB_TYPE.MAIL_WHITELIST,
  MODULE_TAB_TYPE.ASSET_TYPES,
];

function ProjectSetting() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Query params enter from outside the type system; the backend route schemas validate this id.
  // `get()` returns `null` when absent, which every consumer below already tolerates. Erased.
  const projectId = searchParams.get("projectId") as ObjectIdString;
  const projectData = useSelector((state: RootState) => state.projectData);
  const customModules = useSelector((state: RootState) => state.customModules);
  const dispatch = useDispatch();
  const getProjectSettingObject = useSelector((state: RootState) => state.getProjectSettingObject);

  const [settingContent, setSettingContent] = useState(searchParams.get("tab") || "general_information");
  const settingType = searchParams.get("settingType");
  const resourceType = searchParams.get("resourceType");
  const [showSettingsContent, setShowSettingsContent] = useState(false);

  const init = useCallback(async () => {
    dispatch(setSidebarSelectedCell("Settings"));
    dispatch(setShowStickySidebar(true));
  }, []);

  const viewPermissionMapper: Record<string, boolean> = useMemo(
    () =>
      ({
        resource_type: true,
        currency: true,
        spaces: _.get(getProjectSettingObject, "projectSpaces.isVisible", false),
        roles: _.get(getProjectSettingObject, "roles.isVisible", false),
        users: _.get(getProjectSettingObject, "projectUsers.isVisible", false),
        groups: _.get(getProjectSettingObject, "groups.isVisible", false),
        teams: _.get(getProjectSettingObject, "teams.isVisible", false),
        inspection_location: _.get(getProjectSettingObject, "location.isVisible", false),
        WBS: _.get(getProjectSettingObject, "WBS.isVisible", false),
        general_information: _.get(getProjectSettingObject, "projectInfo.isVisible", false),
        formTemplates: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
        space: _.get(getProjectSettingObject, "projectSpaces.isVisible", false),
        assetTypes: _.get(getProjectSettingObject, "assetTypes.isVisible", false),
        projectModules: true,

        // Sub-tabs reached through the Modules page. These must be gated on their own
        // permission, not on `projectModules` (which is always true) — otherwise the
        // PermissionGate is effectively open and any user can reach them via query params.
        formFlows: true,

        reviewFlows: true,
        mailConfiguration: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
        mailVisibility: _.get(getProjectSettingObject, "formTemplate.isVisible", false),

        // `_.get` with a dotted path over a typed object returns `unknown`, but every entry here
        // defaults to `false`, so the values really are booleans. One erased assertion for all of them.
        mailWhitelist: _.get(getProjectSettingObject, "formTemplate.isVisible", false),
      }) as Record<string, boolean>,
    [getProjectSettingObject],
  );

  useEffect(() => {
    // Route keys arrive from query params, so an unmapped value is always possible. Fall back to a
    // readable form of the key itself ("someTab" → "Some Tab") rather than showing it raw; only the
    // mapped labels exist in en.json, which is why the fallback is explicitly untranslated.
    const getSettingLabel = (value: string, labels: Record<string, TranslationKey>) =>
      labels[value] ? t(labels[value]) : untranslated(_.startCase(value));

    let resourceName = "";
    if (resourceType) {
      const actualResoureType = resourceType.toUpperCase();
      if (defaultModuleNamesMapper[actualResoureType]) {
        resourceName = defaultModuleNamesMapper[actualResoureType];
      } else {
        resourceName = customModules.find((module) => module.resourceType === actualResoureType)?.name || "";
      }
    }
    dispatch(
      setBreadCrumbData([
        {
          text: t("Projects"),
          link: "/projects",
        },
        {
          text: projectData.description,
          link: getProjectDashboardPageUrl(projectId),
          showTooltip: true,
          tooltipData: projectData.description,
        },
        {
          text: t("Settings"),
        },
        ...(resourceType && settingType
          ? [
              {
                text: getSettingLabel(settingContent, SETTING_CONTENT_LABELS),
                link: getProjectSettingsPageUrl({ projectId, tab: "projectModules" }),
              },
              {
                text: resourceName,
              },
              {
                text: getSettingLabel(settingType, SETTING_TYPE_LABELS),
              },
            ]
          : [
              {
                text: getSettingLabel(settingContent, SETTING_CONTENT_LABELS),
              },
            ]),
      ]),
    );
  }, [projectData.description, settingContent, projectId, settingType, resourceType, customModules]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (getProjectSettingObject && Object.keys(getProjectSettingObject).length) {
      setShowSettingsContent(true);
    }
  }, [getProjectSettingObject]);

  const getComponentToRender = () => {
    let Component = SETTINGS_COMPONENTS[settingContent];
    if (settingContent === "spaces" && searchParams.get("spaceId")) {
      Component = SETTINGS_COMPONENTS["space"];
    } else if (settingContent === "users" && searchParams.get("userId")) {
      Component = SETTINGS_COMPONENTS["userSpaces"];
    } else if (settingContent === MODULE_SETTING_TYPE.PROJECT_MODULES && searchParams.get("settingType")) {
      const comType = searchParams.get("settingType") || "";
      if (PROJECT_MODULE_TAB_TYPES.includes(comType)) {
        Component = SETTINGS_COMPONENTS[comType];
      }
    }
    return Component;
  };

  // The component actually rendered for a Modules sub-tab is the one named by
  // `settingType`, so the permission gate must key on that effective tab — not on
  // `settingContent`, which stays "projectModules" and is always allowed.
  const getGatedSettingKey = (): string => {
    if (settingContent === MODULE_SETTING_TYPE.PROJECT_MODULES) {
      const comType = searchParams.get("settingType") || "";
      if (PROJECT_MODULE_TAB_TYPES.includes(comType)) {
        return comType;
      }
    }
    return settingContent;
  };

  const renderSettingContent = () => {
    if (!showSettingsContent || !settingContent) return null;
    const Component = getComponentToRender();
    if (!Component) return null;
    return (
      <Suspense fallback={<></>}>
        <PermissionGate allowed={viewPermissionMapper[getGatedSettingKey()]}>
          <Component projectId={projectId} />
        </PermissionGate>
      </Suspense>
    );
  };

  return (
    <GridPageLayout showHeader={false}>
      <div className="h-full w-full overflow-auto">
        <div className="bg-white flex overflow-auto w-full h-full">
          <LeftSidebar setSettingContent={setSettingContent} settingContent={settingContent} />
          <div className="w-full h-full overflow-auto">{renderSettingContent()}</div>
        </div>
      </div>
    </GridPageLayout>
  );
}

export default ProjectSetting;
